/**
 * AppShell — الإطار الوحيد للتطبيق.
 *
 * يحتوي: تنويه البيانات التجريبية · شريط بدون اتصال · الملاحة · Toast ·
 * جلسة التقييم المشتركة بين «قيّم أرض» و«النتيجة».
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { color } from '@/design/tokens';
import { BottomNavigation, Sidebar } from './Navigation';
import { Footer } from './Footer';
import { FirstVisitTour } from '@/components/guide/FirstVisitTour';
import { OfflineBanner } from '@/components/states/States';
import { Toast } from '@/components/primitives/Primitives';
import { useMarketDataContext } from '@/providers';
import { useValuation, type ValuationApi } from '@/services/market';
import type { DemoScenario } from '@/providers/demo/DemoMarketDataProvider';

// ------------------------------------------------------------------ Toast

const ToastContext = createContext<(message: string) => void>(() => {});
export function useToast(): (message: string) => void {
  return useContext(ToastContext);
}

// -------------------------------------------------------- Valuation session

const ValuationSessionContext = createContext<ValuationApi | null>(null);
export function useValuationSession(): ValuationApi {
  const ctx = useContext(ValuationSessionContext);
  if (!ctx) throw new Error('useValuationSession must be used inside <AppShell>.');
  return ctx;
}

// ------------------------------------------------------------ Demo banner

const SCENARIOS: { value: DemoScenario; label: string }[] = [
  { value: 'normal', label: 'الحالة: عادي' },
  { value: 'low-confidence', label: 'الحالة: ثقة منخفضة' },
  { value: 'partial', label: 'الحالة: بيانات جزئية' },
  { value: 'no-data', label: 'الحالة: لا بيانات كافية' },
  { value: 'error', label: 'الحالة: خطأ' },
];

/**
 * مؤشر مصدر البيانات — LIVE أو DEMO.
 * وجوده إلزامي حتى لا يُخلط بين البيانات الحقيقية والتجريبية.
 */
function DataSourceBanner(): JSX.Element {
  const { mode, scenario, setScenario } = useMarketDataContext();
  const isDemo = mode === 'DEMO';

  return (
    <div
      data-testid={isDemo ? 'demo-banner' : 'live-banner'}
      data-data-source={mode}
      style={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 12,
        padding: '7px 16px',
        background: color.ink900,
        borderBottom: `1px solid ${color.hairlineSoft}`,
        fontSize: 11.5,
        color: color.label,
      }}
    >
      <span
        style={{
          padding: '3px 9px',
          border: `1px solid ${isDemo ? 'rgba(240,162,75,.32)' : 'rgba(0,229,199,.3)'}`,
          borderRadius: 5,
          color: isDemo ? color.amber : color.teal,
          fontWeight: 600,
          whiteSpace: 'nowrap',
          letterSpacing: '.08em',
        }}
      >
        {isDemo ? 'DEMO' : 'رسمي'}
      </span>
      <span style={{ color: color.textSubtle }}>
        {isDemo
          ? 'بيانات تجريبية — لا تمثل بيانات سوق فعلية ولا تُستخدم في أي قرار.'
          : 'صفقات أراضٍ من السجل الرسمي — تُحدَّث دورياً، لا لحظياً.'}
      </span>
      <span style={{ flex: 1 }} />
      {scenario ? (
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <span className="nq-micro">معاينة الحالات</span>
          <select
            aria-label="معاينة حالات الواجهة"
            value={scenario}
            onChange={(e) => setScenario(e.target.value as DemoScenario)}
            style={{
              padding: '4px 8px',
              fontSize: 11.5,
              background: color.surface700,
              border: `1px solid ${color.hairline}`,
              borderRadius: 6,
              color: color.textMuted,
              cursor: 'pointer',
            }}
          >
            {SCENARIOS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </label>
      ) : null}
    </div>
  );
}

// ----------------------------------------------------------------- Offline

function useOffline(): boolean {
  const [offline, setOffline] = useState<boolean>(
    typeof navigator !== 'undefined' ? !navigator.onLine : false,
  );
  useEffect(() => {
    const on = (): void => setOffline(false);
    const off = (): void => setOffline(true);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return offline;
}

// ----------------------------------------------------------------- Shell

export function AppShell({ children }: { children?: ReactNode }): JSX.Element {
  const offline = useOffline();
  const valuation = useValuation();
  const [toast, setToast] = useState<string | null>(null);

  const showToast = useCallback((message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(null), 2600);
  }, []);

  /**
   * كل انتقال يبدأ من أعلى الصفحة.
   *
   * العلّة التي أوجبته: المتصفّح يحتفظ بموضع التمرير عبر الانتقال. فمن يضغط
   * «احسب التقييم» — وهو ممرَّر للأسفل بالضرورة، لأن الزر أسفل النموذج —
   * ينتظر خمس ثوانٍ ثم يهبط على تذييل صفحة النتيجة بدل الحكم الذي جاء
   * لأجله. وهذا يبدّد النقطة التي يقوم عليها المنتج كله.
   */
  const { pathname } = useLocation();
  useEffect(() => {
    const scroller = document.scrollingElement ?? document.documentElement;
    scroller.scrollTop = 0;
    scroller.scrollLeft = 0;
  }, [pathname]);

  const toastValue = useMemo(() => showToast, [showToast]);

  return (
    <ToastContext.Provider value={toastValue}>
      <ValuationSessionContext.Provider value={valuation}>
        <DataSourceBanner />
        {offline ? <OfflineBanner /> : null}
        <div className="nq-shell">
          <Sidebar />
          <main className="nq-main">
            {children ?? <Outlet />}
            <Footer />
          </main>
        </div>
        <BottomNavigation />
        <FirstVisitTour />
        {toast ? <Toast message={toast} /> : null}
      </ValuationSessionContext.Provider>
    </ToastContext.Provider>
  );
}
